import { type ReactElement } from 'react';
import { type DispatchDecision, type DispatchDecisionCandidate } from '@vello/api-client';
import { formatDistance, formatDuration } from '@vello/format';
import { Badge, Button, EmptyState, Grid, InlineRow, ListTable, Notice, Panel, Row, SectionTitle, Stack, Text, type Column } from '@vello/web-theme';
import { RANKING_FACTORS, dispatchDecisionsAuthority, factorsOf, formatMarketInstant, hasShadowRank, isRankedCandidate, isSystemActor, marketTimeZone, trailReasonText } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { documentTypeName } from '../i18n/document-type';
import { PersonName } from './person-name';
import { DetailRow } from './detail-row';
import { LoadingBlock } from './loading-block';
import { useRead } from './use-read';

/**
 * D587 (DevMobility "Análise Algoritmo"): the ride's dispatch decision trail (D586, ADR-0110) — every planning decision, oldest
 * first, with every driver it weighed: the ranked ones by place with their score and each factor's weighted contribution, then
 * the ones left out with every reason. Shown to an operator who may read dispatch at a market; the server authorizes the read
 * at the ride's market. A ride dispatched before the trail existed (m0199) has none — the panel says so.
 */
export function DispatchDecisionsPanel({ rideId, marketId }: { readonly rideId: string; readonly marketId: string }): ReactElement | null {
  const { runtime, grants, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, has, fail } = useVocab();
  const authority = dispatchDecisionsAuthority(grants);
  const { state, reload } = useRead(authority.mayView ? () => runtime.ops.dispatchDecisions(rideId) : undefined, [runtime, rideId]);
  // A document reason names a type of the market's KYC catalog (data, not a closed list): read the market's types only when one is shown.
  const needsTypes = state?.status === 'ready' && state.value.decisions.some((d) => d.candidates.some((c) => c.excludedReasons.some((r) => r.startsWith('DOCUMENT_'))));
  const { state: typesState } = useRead(needsTypes ? () => runtime.driverRegistry.documents({ marketId, limit: 1 }) : undefined, [runtime, marketId, needsTypes]);
  const docTypes = typesState?.status === 'ready' ? typesState.value.types : undefined;

  if (!authority.mayView) return null;

  const tz = marketTimeZone(marketId, markets);
  const ctx = { locale: lang, timeZone: 'UTC', measurement: 'metric' } as const;
  const points = new Intl.NumberFormat(lang, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const percent = new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 });
  const words = { v, has, documentType: (code: string) => documentTypeName(docTypes, code, lang, v) };
  const dash = <Text role="caption" tone="secondary">—</Text>;
  const num = (n: number | undefined, f: (x: number) => string): ReactElement => (n === undefined || !Number.isFinite(n) || n < 0 ? dash : <span className="tnum">{f(n)}</span>);

  // D591: the columns follow what the decision weighed - the completion factor and the SHADOW place appear only when it recorded them.
  const columnsOf = (d: DispatchDecision): readonly Column<DispatchDecisionCandidate>[] => [
    { key: 'place', header: t('ride.decisions.col.place'), numeric: true, width: 'minmax(72px, .5fr)', render: (c) => (c.place !== undefined ? <span className="tnum">{c.place}</span> : dash) },
    {
      key: 'driver',
      header: t('ride.decisions.col.driver'),
      width: 'minmax(200px, 1.6fr)',
      render: (c) => (
        <Stack gap={1}>
          <Text role="caption"><PersonName id={c.driverId} /></Text>
          <InlineRow gap={1}>
            {c.offered && <Badge tone="success">{t('ride.decisions.offered')}</Badge>}
            {c.favorite === true && <Badge>{t('ride.decisions.favorite')}</Badge>}
            {!isRankedCandidate(c) && <Badge tone="warning">{t('ride.decisions.excluded')}</Badge>}
          </InlineRow>
        </Stack>
      ),
    },
    {
      key: 'score',
      header: t('ride.decisions.col.score'),
      numeric: true,
      width: 'minmax(96px, .7fr)',
      render: (c) =>
        c.score === undefined ? dash : (
          <Stack gap={1}>
            <strong className="tnum">{points.format(c.score)}</strong>
            {(c.scoreReasons ?? []).map((r) => <Text role="caption" tone="secondary" key={r}>{v('problem.dispatch', r)}</Text>)}
          </Stack>
        ),
    },
    ...(hasShadowRank(d)
      ? [{
          key: 'shadow',
          header: t('ride.decisions.col.shadow'),
          numeric: true,
          width: 'minmax(130px, .9fr)',
          render: (c: DispatchDecisionCandidate) =>
            c.shadow === undefined ? dash : (
              <Stack gap={1}>
                <span className="tnum">{c.shadow.place} · {points.format(c.shadow.score)}</span>
                {c.shadow.offered && <Badge>{t('ride.decisions.shadow.wouldOffer')}</Badge>}
              </Stack>
            ),
        } satisfies Column<DispatchDecisionCandidate>]
      : []),
    ...factorsOf(d).map((f): Column<DispatchDecisionCandidate> => ({
      key: `factor-${f}`,
      header: v('dispatch.ranking_factor', f),
      numeric: true,
      width: 'minmax(96px, .7fr)',
      render: (c) => {
        const x = c.components?.[f];
        return x !== undefined && Number.isFinite(x) ? <span className="tnum">{points.format(x)}</span> : dash;
      },
    })),
    { key: 'distance', header: t('ride.decisions.col.distance'), numeric: true, width: 'minmax(110px, .8fr)', render: (c) => num(c.pickupDistanceMeters, (m) => formatDistance(m, ctx)) },
    { key: 'eta', header: t('ride.decisions.col.eta'), numeric: true, width: 'minmax(100px, .7fr)', render: (c) => num(c.etaSeconds, (s) => formatDuration(s, ctx)) },
    {
      key: 'reasons',
      header: t('ride.decisions.col.reasons'),
      width: 'minmax(220px, 1.8fr)',
      render: (c) =>
        c.excludedReasons.length === 0 ? dash : (
          <Stack gap={1}>
            {c.excludedReasons.map((r) => <Text role="caption" key={r}>{trailReasonText(r, words)}</Text>)}
          </Stack>
        ),
    },
  ];

  const weightsLine = (weightsBasisPoints: Readonly<Record<string, number>>): string =>
    RANKING_FACTORS.filter((f) => weightsBasisPoints[f] !== undefined)
      .map((f) => `${v('dispatch.ranking_factor', f)} ${percent.format((weightsBasisPoints[f] ?? 0) / 10_000)}`)
      .join(' · ');
  const weights = (d: DispatchDecision): string => weightsLine(d.ranking.weightsBasisPoints);

  const decision = (d: DispatchDecision): ReactElement => (
    <Stack gap={3} key={d.decisionId} data-testid="dispatch-decision">
      <SectionTitle level={3} size="sub" actions={<Badge tone={d.outcome === 'wave' ? 'success' : d.outcome === 'no_driver' ? 'danger' : 'warning'}>{v('dispatch.wave_decision_outcome', d.outcome)}</Badge>}>
        {t('ride.decisions.wave', { n: d.waveNumber })}
      </SectionTitle>
      <Grid min="sm" gap={3}>
        <DetailRow label={t('ride.decisions.meta.at')} mono>{formatMarketInstant(d.decidedAt, tz, lang, 'datetime-seconds')}</DetailRow>
        <DetailRow label={t('ride.decisions.meta.radius')} mono>{d.radiusMeters !== undefined ? formatDistance(d.radiusMeters, ctx) : undefined}</DetailRow>
        <DetailRow label={t('ride.decisions.meta.drivers')} mono={false}>{t('ride.decisions.meta.driversValue', { offered: d.offeredCount, count: d.candidateCount })}</DetailRow>
        <DetailRow label={t('ride.decisions.meta.actor')} mono={!isSystemActor(d.actor)}>
          {isSystemActor(d.actor) ? v('dispatch.decision_actor', d.actor) : <>{t('ride.decisions.actor.operator')} · <PersonName id={d.actor} /></>}
        </DetailRow>
        <DetailRow label={t('ride.decisions.meta.policy')} mono>{d.policyVersion}</DetailRow>
        <DetailRow label={t('ride.decisions.meta.weights')} mono={false}>{weights(d)}</DetailRow>
        {d.ranking.scoring !== undefined && (
          <>
            <DetailRow label={t('ride.decisions.meta.scoring')} mono={false}>{`${v('dispatch.ranking_mode', d.ranking.scoring.mode)} · ${v('problem.dispatch', d.ranking.scoring.reasonCode)}`}</DetailRow>
            <DetailRow label={t('ride.decisions.meta.scoringPolicy')} mono>{d.ranking.scoring.policyVersion}</DetailRow>
            {d.ranking.scoring.weightsBasisPoints !== undefined && <DetailRow label={t('ride.decisions.meta.scoringWeights')} mono={false}>{weightsLine(d.ranking.scoring.weightsBasisPoints)}</DetailRow>}
          </>
        )}
      </Grid>
      <ListTable
        columns={columnsOf(d)}
        rows={d.candidates}
        rowKey={(c) => c.driverId}
        countLabel={t('ride.decisions.candidates')}
        empty={<EmptyState title={t('ride.decisions.candidates.empty')} />}
        minWidth={1480}
      />
    </Stack>
  );

  return (
    <Panel><Stack gap={4} data-testid="dispatch-decisions">
      <Row gap={3} align="center" justify="between" wrap>
        <div>
          <Text as="h2" role="title">{t('ride.decisions.title')}</Text>
          <Text as="p" role="caption" tone="secondary">{t('ride.decisions.subtitle')}</Text>
        </div>
      </Row>
      {state === undefined || state.status === 'loading' ? (
        <LoadingBlock />
      ) : state.status === 'error' ? (
        <Stack gap={3}>
          <Notice tone="danger">{t('ride.decisions.error', { code: fail({ code: state.code }) })}</Notice>
          <InlineRow><Button variant="secondary" icon="rotateCcw" onClick={reload}>{t('ride.decisions.retry')}</Button></InlineRow>
        </Stack>
      ) : state.value.decisions.length === 0 ? (
        <EmptyState title={t('ride.decisions.empty')} detail={t('ride.decisions.empty.detail')} />
      ) : (
        <Stack gap={5}>{state.value.decisions.map(decision)}</Stack>
      )}
    </Stack></Panel>
  );
}
